/**
 * BridgeTimelineSection — customer-fidelity bridge timeline summary.
 *
 * Phase 8c customer surface is a strictly-filtered list of `result=created`
 * rows with EXACTLY these 6 fields:
 *   {result, category, bucket, vendor_name, inquiry_id, created_at}
 *
 * We render a compact "started conversation with X" feed — no rejected
 * rows, no duplicates, no worker IDs, no source_brief_card_id, no
 * rejection reasons. This is enforced by the projection layer; the
 * component still treats every row as authoritative customer-safe.
 */

import React from 'react';
import { MessageSquare } from 'lucide-react';

function fmtRelative(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const now = Date.now();
    const diff = Math.max(0, now - d.getTime());
    const hours = Math.round(diff / 3.6e6);
    if (hours < 1) return 'just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (_) {
    return null;
  }
}

export default function BridgeTimelineSection({ timeline }) {
  // Empty timeline → nothing to show. Calm by absence.
  if (!Array.isArray(timeline) || timeline.length === 0) return null;
  return (
    <div data-testid="ops-bridge-timeline">
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
        Recent conversations
      </p>
      <ul className="space-y-2">
        {timeline.slice(0, 5).map((row, idx) => {
          // Defensive — the projection guarantees result==="created" for
          // customer view; we still skip anything that slipped through.
          if (row.result !== 'created') return null;
          const when = fmtRelative(row.created_at);
          return (
            <li
              key={`${row.inquiry_id || idx}`}
              className="flex items-start gap-3 text-sm"
              data-testid={`ops-bridge-row-${idx}`}
            >
              <MessageSquare className="w-4 h-4 text-violet-400 shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-800 truncate">
                  Started a conversation with{' '}
                  <span className="font-medium text-gray-900">
                    {row.vendor_name || 'a recommended vendor'}
                  </span>
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {row.category}
                  {row.bucket && row.bucket !== 'top_pick' && (
                    <> · {row.bucket.replace('_', ' ')}</>
                  )}
                  {when && <> · {when}</>}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
